/**
 * Job status color overrides: persistent per-status accent hex in localStorage.
 * Defaults match JOB_STATUS_HEX_COLORS / JOB_STATUS_CHIP_STYLES in jobStatuses.js.
 * Edit in Settings; consumed via getStatusChipStyles in jobStatuses.js.
 */

const STORAGE_KEY = 'jobStatusColors';

/** Default accent hex per status (borderColor in JOB_STATUS_CHIP_STYLES). */
const JOB_STATUS_DEFAULT_HEX = {
  1: '#ca8a04',
  2: '#0d9488',
  3: '#16a34a',
  4: '#2563eb',
  5: '#4f46e5',
  6: '#ea580c',
  7: '#9333ea',
  8: '#e11d48',
  9: '#059669',
  10: '#c026d3',
  11: '#7c3aed',
  12: '#0891b2',
  13: '#65a30d',
  14: '#dc2626',
  15: '#92400e',
  16: '#0284c7',
};

export const JOB_STATUS_COLORS_CHANGED_EVENT = 'jobStatusColorsChanged';

const DEFAULT_STATUS_HEX = '#6b7280';

function notifyJobStatusColorsChanged() {
  try {
    window.dispatchEvent(new Event(JOB_STATUS_COLORS_CHANGED_EVENT));
  } catch {
    // ignore (SSR/tests)
  }
}

function hexToRgb(hex) {
  if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return null;
  const n = parseInt(hex.slice(1), 16);
  return {
    r: ((n >> 16) & 0xff) / 255,
    g: ((n >> 8) & 0xff) / 255,
    b: (n & 0xff) / 255,
  };
}

function rgbToHex(r, g, b) {
  const rr = Math.round(Math.max(0, Math.min(1, r)) * 255);
  const gg = Math.round(Math.max(0, Math.min(1, g)) * 255);
  const bb = Math.round(Math.max(0, Math.min(1, b)) * 255);
  return '#' + [rr, gg, bb].map((x) => x.toString(16).padStart(2, '0')).join('');
}

function lightenWithWhite(hex, whiteRatio) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const t = 1 - whiteRatio;
  return rgbToHex(
    rgb.r * t + whiteRatio,
    rgb.g * t + whiteRatio,
    rgb.b * t + whiteRatio
  );
}

function darken(hex, factor) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return rgbToHex(rgb.r * factor, rgb.g * factor, rgb.b * factor);
}

/**
 * @returns {{ [statusId: string]: string }} Map of status id -> hex color
 */
export function getStoredJobStatusColors() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const result = {};
    for (const [id, hex] of Object.entries(parsed)) {
      if (typeof hex === 'string' && /^#[0-9A-Fa-f]{6}$/.test(hex)) {
        result[String(id)] = hex;
      }
    }
    return result;
  } catch {
    return {};
  }
}

function saveJobStatusColors(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

/**
 * Default accent hex for a status (border color from built-in palette).
 * @param {number} statusId
 * @returns {string}
 */
export function getDefaultJobStatusColor(statusId) {
  const hex = JOB_STATUS_DEFAULT_HEX[Number(statusId)];
  return hex && /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : DEFAULT_STATUS_HEX;
}

/**
 * Stored override hex only, or null if using defaults.
 * @param {number} statusId
 * @returns {string | null}
 */
export function getJobStatusColorOverrideHex(statusId) {
  const map = getStoredJobStatusColors();
  const hex = map[String(statusId)];
  return hex && /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : null;
}

export function hasJobStatusColorOverride(statusId) {
  return getJobStatusColorOverrideHex(statusId) != null;
}

/**
 * Effective accent hex (override or default).
 * @param {number} statusId
 * @returns {string}
 */
export function getJobStatusColor(statusId) {
  return getJobStatusColorOverrideHex(statusId) ?? getDefaultJobStatusColor(statusId);
}

/**
 * Derive saturated status chip styles from accent hex (custom overrides only).
 * @param {string} hex
 * @returns {{ bgColor: string, textColor: string, borderColor: string }}
 */
export function getStatusChipStylesFromHex(hex) {
  const h = hex && /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : DEFAULT_STATUS_HEX;
  return {
    bgColor: lightenWithWhite(h, 0.45),
    textColor: darken(h, 0.35),
    borderColor: h,
  };
}

/**
 * @param {number} statusId
 * @param {string} hexColor
 */
export function setJobStatusColor(statusId, hexColor) {
  const hex =
    typeof hexColor === 'string' && /^#[0-9A-Fa-f]{6}$/.test(hexColor)
      ? hexColor
      : getDefaultJobStatusColor(statusId);
  const map = getStoredJobStatusColors();
  map[String(statusId)] = hex;
  saveJobStatusColors(map);
  notifyJobStatusColorsChanged();
}

/**
 * @param {number} statusId
 */
export function resetJobStatusColor(statusId) {
  const map = getStoredJobStatusColors();
  delete map[String(statusId)];
  saveJobStatusColors(map);
  notifyJobStatusColorsChanged();
}

export function resetAllJobStatusColors() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  notifyJobStatusColorsChanged();
}
